import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Typography,
  IconButton,
  Stack,
  Tooltip,
  Divider,
  Button,
  Box,
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FetchAutoComplete from '../../../Components/Inputs/FetchAutoComplete';
import { applyCallFilter } from '../../../Helpers/Filters/constants';
import useAPI from '../../../api';
import DbeDatePicker from '../../../Components/Inputs/DbeDatePicker';
import { formatListItemDate } from '../../../Helpers/dates';

/**
 * Filters menu child for WavePeriod filter selection:
 *
 * @param {Object} props - Component props.
 * @param {String} props.wavePeriod - String. Selected wave period string filter; ES: 202311 is November 23.
 * @param {Function} props.onConfirmPeriod - Callback. Function executed on confirm click that sets wavePeriod filter with a new value.
 * @param {Function} props.onClose - Function. Executed on Filters menu closure.
 *
 * @returns {React.JSX.Element}
 */

const WelcomeCallsFilters = function WelcomeCallsFilters({
  defaultValues = null,
  onConfirm = () => {},
  onClose = () => {},
}) {
  const intl = useIntl();
  const API = useAPI();

  const [liquidationDate, setLiquidationDate] = useState(null);
  const [dealer, setDealer] = useState('');
  const [offer, setOffer] = useState('');
  const [selectedCallFilter, setSelectedCallFilter] = useState(
    JSON.parse(localStorage.getItem('lastCallInCurrentMonth'))
  );

  useEffect(() => {
    if (defaultValues) {
      const dateString = defaultValues?.liquidationDate; // DD/MM/YYYY format
      const [day, month, year] = dateString?.split('/').map(Number) || [];
      const formattedDate =
        day && month && year ? new Date(year, month - 1, day) : null;

      setLiquidationDate(formattedDate ?? null);
      setDealer(
        JSON.parse(localStorage.getItem('dealer')) || defaultValues?.dealer
      );
      setOffer(JSON.parse(localStorage.getItem('tag')) || defaultValues?.tag);
    }
  }, [defaultValues]);

  const getDealerOptions = (searchValue, onSuccess, onFailure) =>
    API.getDealerOptions(searchValue, onSuccess, onFailure);

  const getOfferOptions = (searchValue, onSuccess, onFailure) =>
    API.getOfferOptions('Welcome', searchValue, onSuccess, onFailure);

  const selectCallFilter = (value) => {
    setSelectedCallFilter(value);
  };

  const handleChangeDealer = (e, newValue) => {
    setDealer(newValue);
  };
  const handleChangeOffer = (e, newValue) => {
    setOffer(newValue);
  };

  const handleChangeLiquidationDate = (newDate) => {
    setLiquidationDate(newDate);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '400px',
        gap: '16px',
      }}
    >
      <DbeDatePicker
        variant="outlined"
        label={intl.messages.LIQUIDATIONDATE}
        date={liquidationDate}
        onChange={handleChangeLiquidationDate}
      />
      <FetchAutoComplete
        id="dealer-automplete"
        label={intl.messages.DEALER}
        width="100%"
        selectedValue={dealer}
        onChangeSelectedValue={handleChangeDealer}
        fetchCallback={getDealerOptions}
      />
      <FetchAutoComplete
        id="offer-automplete"
        label={intl.messages.OFFER}
        width="100%"
        selectedValue={offer}
        onChangeSelectedValue={handleChangeOffer}
        fetchCallback={getOfferOptions}
      />
      <Divider />
      <Typography variant="overline" sx={{ opacity: '60%' }}>
        {intl.messages.MONTH_CALLED_INDICATOR}
      </Typography>
      <Stack
        id="user-status-btns"
        direction="row"
        spacing={1}
        sx={{
          justifyContent: "space-around",
          mb: "16px"
        }}>
        <Tooltip title={intl.messages.SHOW_CUSTOMERS_ALREADY_CALLED}>
          <IconButton size="large" onClick={() => selectCallFilter(1)}>
            <CallIcon
              color={selectedCallFilter === 1 ? 'success' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.messages.SHOW_CUSTOMERS_NOT_CALLED}>
          <IconButton size="large" onClick={() => selectCallFilter(0)}>
            <PhoneDisabledIcon
              color={selectedCallFilter === 0 ? 'tocall' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.messages.SHOW_ALL}>
          <IconButton size="large" onClick={() => selectCallFilter(null)}>
            <FilterAltOffIcon
              color={selectedCallFilter === null ? 'primary' : 'disabled'}
            />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          position: "sticky",
          bottom: 0
        }}>
        <Button
          id="confirm-welcomeFilters-btn"
          size="large"
          onClick={() => {
            const callFilter = applyCallFilter(false, selectedCallFilter);
            onConfirm({
              dealer,
              tag: offer,
              liquidationDate: formatListItemDate(liquidationDate),
              ...callFilter,
            });
            onClose();
          }}
        >
          {intl.messages.CONFIRM}
        </Button>
      </Box>
    </div>
  );
};

WelcomeCallsFilters.propTypes = {
  defaultValues: PropTypes.object,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
};

export default WelcomeCallsFilters;
